/**
 * TC dashboard v2 — Finance tab model (TC-DASHBOARD-V2-DESIGN §4.2). Shape = the N11 contract for
 * GET /api/dashboard/finance; assembled today from the finance reports, the P&L and the dashboard endpoints.
 * Numbers come from the same report endpoints the Reports pages use, so the dashboard matches them.
 */

import ApiService from '../api.service';
import { unwrapData } from '../../utils/apiConvert';
import { financeReportsService } from '../accounting/financeReports.service';
import { profitLossService } from '../accounting/profitLoss.service';
import { delta, ownMoney, overduePct } from '../../utils/dashboard/metrics';
import { previousRange, toIso } from '../../utils/dashboard/period';

const n = (v) => Number(v) || 0;
const NOT_STARTED = /ledger has not started/i;
// Spring puts "Bad Request" in `error` and the reason in `message`; the app's own errors use either field
const notStarted = (err) => err?.response?.status === 400
  && NOT_STARTED.test(`${err?.response?.data?.error || ''} ${err?.response?.data?.message || ''}`);
const failText = (err) => (err?.response?.status === 403 ? 'No access' : 'Could not load');

// What the TC owes, from the health check's ledger figures
const OWED_CHECKS = ['PAYABLES', 'THAKLIS', 'VAT', 'COMMISSIONS'];

const ACTION_LABELS = {
  REVIEW_ACCOUNT: 'Review', APPROVE_TOPUP: 'Approve', PAY_EXPENSE: 'Pay', PAY_COMMISSIONS: 'Pay', FILE_VAT: 'File',
  PAY_VAT: 'Pay', REVIEW_CHEQUE: 'Review', RUN_DEPRECIATION: 'Run',
};

// The server's days_1_30 … keys arrive camelCased as days130 …
const buckets = (row = {}) => ({
  current: n(row.current),
  d1to30: n(row.days130 ?? row.days_1_30),
  d31to60: n(row.days3160 ?? row.days_31_60),
  d60plus: n(row.days6190 ?? row.days_61_90) + n(row.daysOver90 ?? row.days_over_90),
});

const oldestBucket = (b) => (b.d60plus ? '60+ days' : b.d31to60 ? '31–60 days' : b.d1to30 ? '1–30 days' : 'Current');

export const loadFinance = async (range, today = new Date(), mock = {}) => {
  const prev = previousRange(range);
  const from30 = toIso(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29));
  const [cashR, advR, agingR, healthR, plR, plPrevR, idleR, flowR, thaklisR, floatR] = await Promise.allSettled([
    financeReportsService.getCashPosition(),
    financeReportsService.getCustomerAdvancesDebtors(),
    financeReportsService.getReceivablesAging(),
    financeReportsService.getHealthCheck(),
    profitLossService.get({ from: range.from, to: range.to }),
    profitLossService.get({ from: prev.from, to: prev.to }),
    ApiService.get('/api/customer-wallets/idle'),
    financeReportsService.getDailyCashRange({ from: from30, to: toIso(today) }),
    ApiService.get('/api/dashboard/thaklis-wallet'),
    ApiService.get('/api/dashboard/govt-fee-float'),
  ]);
  const errors = {};
  // The whole tab is empty when the TC has no ledger at all (the cash position refuses); a single report that
  // refuses (e.g. the health check before the go-live cutover) only empties its own cards.
  const empty = cashR.status === 'rejected' && notStarted(cashR.reason);
  const notStartedCards = {};
  const value = (r, card) => {
    if (r.status === 'fulfilled') return r.value;
    if (notStarted(r.reason)) notStartedCards[card] = true;
    else if (!empty) errors[card] = failText(r.reason);
    return null;
  };

  const cashPos = value(cashR, 'cash');
  const adv = value(advR, 'wallets');
  const aging = value(agingR, 'aging');
  const health = value(healthR, 'health');
  const pl = value(plR, 'netProfit');
  const plPrev = plPrevR.status === 'fulfilled' ? plPrevR.value : null;

  const flow = value(flowR, 'cashFlow');
  const thaklisBody = thaklisR.status === 'fulfilled' ? unwrapData(thaklisR.value) : null;
  if (!thaklisBody && !empty) errors.thaklisWallet = failText(thaklisR.reason);
  const floatBody = floatR.status === 'fulfilled' ? unwrapData(floatR.value) : null;
  if (!floatBody && !empty) errors.govtFloat = failText(floatR.reason);
  const idleBody = idleR.status === 'fulfilled' ? unwrapData(idleR.value) : null;
  const idle = { count: n(idleBody?.count), amount: n(idleBody?.amount) };

  const walletRows = (adv?.rows || []).map((r) => ({ customerId: r.customerId, name: r.customerName, balance: n(r.credit) }))
    .filter((w) => w.balance > 0).sort((a, b) => b.balance - a.balance);
  const walletTotal = walletRows.reduce((s, w) => s + w.balance, 0);
  const cashTotal = n(cashPos?.totalCash);

  const checks = health?.checks || [];
  const owedFromChecks = checks.filter((c) => OWED_CHECKS.includes(c.id)).reduce((s, c) => s + n(c.books), 0);
  const healthItems = checks.filter((c) => /WARNING|ERROR/i.test(c.status))
    .flatMap((c) => (c.items || []).map((it) => ({ title: it.text, detail: it.amount != null ? n(it.amount) : null, action: { label: ACTION_LABELS[it.action] || 'Open', code: it.action, entityId: it.entityId } })))
    .slice(0, 3);

  const agingTotals = buckets(aging?.totals);
  const owedTotal = agingTotals.current + agingTotals.d1to30 + agingTotals.d31to60 + agingTotals.d60plus;

  return {
    empty,
    cash: { total: cashTotal, customerWallets: walletTotal, ownMoney: ownMoney(cashTotal, walletTotal), accountsCount: (cashPos?.accounts || []).length },
    owed: { total: owedTotal, overduePct: overduePct(agingTotals) },
    youOwe: { total: health ? owedFromChecks + n(cashPos?.cardsOwed) : null },
    netProfit: { amount: n(pl?.netProfit), delta: delta(n(pl?.netProfit), plPrev ? n(plPrev.netProfit) : null) },
    cashFlow: { days: (flow || []).map((d) => ({ date: d.date, in: n(d.cashIn), out: n(d.cashOut) })) },
    health: { passed: n(health?.passed), total: n(health?.total), items: healthItems, empty: !!notStartedCards.health },
    aging: {
      ...agingTotals,
      top: (aging?.rows || []).map((r) => ({ customerId: r.customerId, name: r.customerName, amount: n(r.total), oldest: oldestBucket(buckets(r)) }))
        .sort((a, b) => b.amount - a.amount).slice(0, 3),
    },
    govtFloat: { paid: n(floatBody?.advanced), collected: n(floatBody?.collected), writtenOff: n(floatBody?.writtenOff), float: n(floatBody?.float), failed: !floatBody },
    accounts: (cashPos?.accounts || []).map((a) => ({ id: a.accountId, name: a.name, type: a.type, balance: n(a.balance) })),
    thaklisWallet: { balance: n(thaklisBody?.balance), pendingTopups: n(thaklisBody?.pendingTopups), failed: !thaklisBody },
    wallets: { total: walletTotal, customers: walletRows.length, top: walletRows, idle: idle },
    errors,
  };
};
